<template>
    <section class="skill-accordion">
        <h2 class="skill-accordion__title">По элементам</h2>
        <div v-for="section in sections" :key="section.id" class="skill-section">
            <button
                type="button"
                class="skill-section__header"
                :class="{ 'skill-section__header--open': expanded[section.id] }"
                @click="toggle(section.id)"
            >
                <span class="skill-section__chevron">{{ expanded[section.id] ? '▼' : '▶' }}</span>
                <span class="skill-section__name">{{ section.title }}</span>
                <span class="skill-section__main">{{ section.mainValue }}</span>
            </button>
            <div v-show="expanded[section.id]" class="skill-section__body">
                <div class="skill-section__details">
                    <div v-for="row in section.rows" :key="row.label" class="skill-detail-row">
                        <span class="skill-detail-row__label">{{ row.label }}</span>
                        <span class="skill-detail-row__value" :class="row.type">{{ row.value }}</span>
                    </div>
                </div>
                <button
                    v-if="section.formulaCategory"
                    type="button"
                    class="skill-formula-toggle"
                    @click="toggleFormula(section.id)"
                >
                    {{ formulaOpen[section.id] ? 'Скрыть формулу' : 'Как считается?' }}
                </button>
                <div v-if="formulaOpen[section.id] && section.formulaCategory" class="skill-formula">
                    <EfficiencyFormula :player="formulaPlayer(section)" :category="section.formulaCategory" theme="light" />
                </div>
            </div>
        </div>
        <p v-if="sections.length === 0" class="skill-accordion__empty">Нет данных за выбранный период</p>
    </section>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import EfficiencyFormula from '@/components/best-players/EfficiencyFormula.vue';

const props = defineProps({
    stats: { type: Object, default: () => ({}) },
    position: { type: String, default: null },
});

const expanded = ref({});
const formulaOpen = ref({});

const sections = computed(() => {
    const s = props.stats || {};
    const pos = props.position;
    const list = [];

    if (pos !== 'libero' && s.attack_total > 0) {
        list.push({
            id: 'attack',
            title: 'Атака',
            mainValue: `${s.attack_efficiency}%`,
            formulaCategory: 'attack',
            rows: [
                { label: 'Очки', value: s.attack_kills, type: 'positive' },
                { label: 'Всего', value: s.attack_total, type: 'neutral' },
                { label: 'Ошибки', value: s.attack_errors, type: 'negative' },
                { label: 'Заблокировано', value: s.attack_blocked, type: 'negative' },
                { label: 'За партию', value: s.attack_kills_per_set?.toFixed?.(2) ?? '0', type: 'neutral' },
            ],
        });
    }

    if (pos !== 'libero' && s.serve_total > 0) {
        list.push({
            id: 'serve',
            title: 'Подача',
            mainValue: `${s.serve_efficiency}%`,
            formulaCategory: 'serve',
            rows: [
                { label: 'Эйсы', value: s.serve_aces, type: 'positive' },
                { label: 'Всего', value: s.serve_total, type: 'neutral' },
                { label: 'Ошибки', value: s.serve_errors, type: 'negative' },
                { label: 'За партию', value: s.serve_aces_per_set?.toFixed?.(2) ?? '0', type: 'neutral' },
            ],
        });
    }

    if (pos !== 'libero' && s.block_total > 0) {
        list.push({
            id: 'block',
            title: 'Блок',
            mainValue: `${s.block_efficiency}%`,
            formulaCategory: 'block',
            rows: [
                { label: 'Очки', value: s.block_kills, type: 'positive' },
                { label: 'Всего', value: s.block_total, type: 'neutral' },
                { label: 'Ошибки', value: s.block_errors, type: 'negative' },
            ],
        });
    }

    if ((['libero', 'outside'].includes(pos) || s.reception_total > 0) && s.reception_total > 0) {
        list.push({
            id: 'reception',
            title: 'Приём',
            mainValue: `${s.reception_positive_pct}%`,
            formulaCategory: 'reception',
            rows: [
                { label: 'Позитивных', value: `${s.reception_positive}/${s.reception_total}`, type: 'positive' },
                { label: 'Отличных', value: s.reception_perfect, type: 'positive' },
                { label: 'Эффективность', value: `${s.reception_efficiency}%`, type: 'neutral' },
            ],
        });
    }

    if ((['setter', 'outside'].includes(pos) || s.set_total > 0) && s.set_total > 0) {
        list.push({
            id: 'set',
            title: 'Передача',
            mainValue: `${s.set_efficiency}%`,
            formulaCategory: null,
            rows: [
                { label: 'Отличных', value: `${s.set_excellent}/${s.set_total}`, type: 'positive' },
                { label: 'Ошибки', value: s.set_errors, type: 'negative' },
            ],
        });
    }

    if ((['libero', 'outside'].includes(pos) || s.dig_total > 0) && s.dig_total > 0) {
        list.push({
            id: 'dig',
            title: 'Защита',
            mainValue: `${s.dig_efficiency}%`,
            formulaCategory: null,
            rows: [
                { label: 'Успешных', value: `${s.dig_success}/${s.dig_total}`, type: 'positive' },
            ],
        });
    }

    return list;
});

const defaultExpanded = computed(() => {
    const ids = sections.value.slice(0, 2).map((s) => s.id);
    return ids;
});

watch(
    sections,
    (list) => {
        const next = { ...expanded.value };
        defaultExpanded.value.forEach((id) => {
            if (next[id] === undefined) next[id] = true;
        });
        expanded.value = next;
    },
    { immediate: true }
);

const toggle = (id) => {
    expanded.value = { ...expanded.value, [id]: !expanded.value[id] };
};

const toggleFormula = (id) => {
    formulaOpen.value = { ...formulaOpen.value, [id]: !formulaOpen.value[id] };
};

const formulaPlayer = (section) => {
    const s = props.stats;
    if (section.formulaCategory === 'attack') {
        return {
            attack_kills: s.attack_kills,
            attack_blocked: s.attack_blocked,
            attack_errors: s.attack_errors,
            attack_total: s.attack_total,
            attack_efficiency: s.attack_efficiency,
        };
    }
    if (section.formulaCategory === 'serve') {
        return {
            serve_aces: s.serve_aces,
            serve_errors: s.serve_errors,
            serve_total: s.serve_total,
            serve_efficiency: s.serve_efficiency,
        };
    }
    if (section.formulaCategory === 'block') {
        return {
            block_kills: s.block_kills,
            block_soft: s.block_soft,
            block_remain: s.block_remain,
            block_errors: s.block_errors,
            block_total: s.block_total,
            block_efficiency: s.block_efficiency,
        };
    }
    if (section.formulaCategory === 'reception') {
        return {
            reception_positive: s.reception_positive,
            reception_perfect: s.reception_perfect,
            reception_total: s.reception_total,
            reception_positive_pct: s.reception_positive_pct,
            reception_efficiency: s.reception_efficiency,
        };
    }
    return s;
};
</script>
